import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { FiPlay, FiPause, FiVolume2, FiVolumeX } from "react-icons/fi";

// Playback ticks stay here: the caption and the post's social data do not rerender.
const PostPlayback = forwardRef(function PostPlayback(
  { enabled, onCommand, children },
  ref,
) {
  const [playback, setPlayback] = useState({});
  const [feedback, setFeedback] = useState(false);
  const [scrubbing, setScrubbing] = useState(false);
  const [position, setPosition] = useState(null);
  const timer = useRef(null);
  const dragging = useRef(false);
  const latest = useRef(null);
  latest.current = { enabled, playback, onCommand };
  function reveal() {
    clearTimeout(timer.current);
    setFeedback(true);
    timer.current = setTimeout(() => setFeedback(false), 1500);
  }
  function command(name, data) {
    if (!latest.current.enabled) return;
    latest.current.onCommand(name, data);
    reveal();
  }
  useImperativeHandle(ref, () => ({
    update: (state) => {
      setPlayback(state);
      if (!dragging.current) setPosition(null);
    },
  }));
  useEffect(() => {
    if (!enabled) {
      clearTimeout(timer.current);
      setFeedback(false);
      setScrubbing(false);
      setPosition(null);
    }
    return () => clearTimeout(timer.current);
  }, [enabled]);
  const duration = Number.isFinite(playback.duration)
    ? Math.max(0, playback.duration)
    : 0;
  const time = Math.max(0, Math.min(duration, position ?? playback.time ?? 0));
  const percent = duration ? (time / duration) * 100 : 0;
  const PlayIcon = playback.playing ? FiPause : FiPlay;
  const SoundIcon = playback.muted !== false ? FiVolumeX : FiVolume2;
  return (
    <>
      {enabled && (
        <div className={`post-playback${feedback ? " is-visible" : ""}`}>
          <button
            type="button"
            className="post-playback__tap"
            aria-label={playback.playing ? "Pause video" : "Play video"}
            onClick={() => command("play")}
          >
            <span className="post-playback__feedback" aria-hidden="true">
              <PlayIcon />
            </span>
          </button>
          <button
            type="button"
            className="post-playback__sound"
            aria-label={
              playback.muted !== false ? "Unmute video" : "Mute video"
            }
            onClick={() => command("mute")}
          >
            <SoundIcon />
          </button>
        </div>
      )}
      {(children || enabled) && (
        <div
          className={`post-detail__caption-wrap${children ? "" : " post-detail__caption-wrap--empty"}`}
        >
          {children}
          {enabled && (
            <div
              className={`post-playback__seek${scrubbing ? " is-scrubbing" : ""}`}
            >
              <div className="post-playback__track" aria-hidden="true">
                <span style={{ width: `${percent}%` }} />
                <i style={{ left: `${percent}%` }} />
              </div>
              <input
                type="range"
                min="0"
                max={duration || 1}
                step="0.01"
                value={time}
                disabled={!duration}
                aria-label="Video position"
                aria-valuetext={`${Math.round(time)} of ${Math.round(duration)} seconds`}
                onPointerDown={(event) => {
                  event.stopPropagation();
                  event.currentTarget.setPointerCapture(event.pointerId);
                  dragging.current = true;
                  setScrubbing(true);
                }}
                onPointerUp={() => {
                  dragging.current = false;
                  setScrubbing(false);
                }}
                onPointerCancel={() => {
                  dragging.current = false;
                  setScrubbing(false);
                }}
                onLostPointerCapture={() => {
                  dragging.current = false;
                  setScrubbing(false);
                }}
                // Native range emits a final change after pointerup; seek on input only
                // so releasing cannot seek back to a stale controlled value.
                onChange={() => {}}
                onInput={(event) => {
                  const next = Number(event.target.value);
                  setPosition(next);
                  onCommand("seek", { time: next });
                }}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
});
export default PostPlayback;
